import { apiClient } from '../lib/api-client';
import type { DiscoveryScanStatusResponse } from '@sdd-maturity/shared-types';
import { usePolledStatus } from '../hooks/usePolledStatus';
import { TERMINAL_STATES } from '../lib/scanStatus';
import { ScanStatusCard } from './ScanStatusCard';

interface DiscoveryScanProgressProps {
  executionArn: string;
  onComplete?: () => void;
}

/**
 * Polls Discovery scan status every 5 seconds until terminal state.
 * Mirrors the Delivery ScanProgress component for visual consistency:
 * Status / Started / Completed / Result rows.
 */
export function DiscoveryScanProgress({ executionArn, onComplete }: DiscoveryScanProgressProps) {
  const { status, error } = usePolledStatus<DiscoveryScanStatusResponse>(
    (arn, token) => apiClient.getDiscoveryScanStatus(arn, token),
    executionArn,
    onComplete,
  );

  // Build a "result" line consistent with Delivery's row, using stage counts
  // when available (the Discovery state machine surfaces these on completion).
  let resultText = '';
  if (status && TERMINAL_STATES.has(status.status)) {
    if (status.status === 'SUCCEEDED') {
      const stages = status.stages;
      if (stages?.scored != null) {
        const scored = stages.scored;
        const partial = stages.classified?.partial ?? 0;
        const notSdd = stages.classified?.notSdd ?? 0;
        resultText = `${scored} feature${scored === 1 ? '' : 's'} scored`;
        if (partial > 0) resultText += `, ${partial} partial`;
        if (notSdd > 0) resultText += `, ${notSdd} not-sdd`;
      } else {
        resultText = 'Scan completed successfully';
      }
    } else {
      resultText = status.cause ?? 'Scan did not complete';
    }
  }

  return <ScanStatusCard status={status} error={error} testId="discovery-scan-progress" resultText={resultText} />;
}
